import { AppThunk } from "../../store";
import { providerActions, ProviderQuery } from "./providerSlice";
import { ProviderDTO } from "../../../types/providerTypes";
import { apiFetch } from "../../../utils/api";
import { toPage, withPaging } from "../../../utils/pagination";

export const registerProvider =
  (payload: ProviderDTO): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(providerActions.startLoading());
      await apiFetch("/api/providers/register", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // you may want to return saved provider or token
      dispatch(providerActions.endLoading());
    } catch (e: any) {
      dispatch(providerActions.setError(e.message));
    } finally {
      dispatch(providerActions.endLoading());
    }
  };

const runSearch =
  (query: ProviderQuery, page: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(providerActions.startLoading());
      const params = new URLSearchParams();
      let path: string;
      if (query.kind === "location") {
        params.append("stateId", String(query.stateId));
        if (query.lgaId) params.append("lgaId", String(query.lgaId));
        params.append("serviceId", String(query.serviceId));
        path = `/api/providers/search?${params.toString()}`;
      } else {
        // Distance-sorted search via the backend's geo endpoint: needs a skill
        // *name* and lat/lon instead of numeric ids, and returns a real
        // `distance` per result.
        params.append("skill", query.skillName);
        params.append("lat", String(query.lat));
        params.append("lon", String(query.lon));
        path = `/api/search?${params.toString()}`;
      }

      const data = await apiFetch(withPaging(path, page));
      dispatch(providerActions.setResultsPage({ query, page: toPage(data) }));
    } catch (e: any) {
      dispatch(providerActions.setError(e.message));
    } finally {
      dispatch(providerActions.endLoading());
    }
  };

export const searchProviders = (
  stateId: number,
  lgaId: number | null,
  serviceId: number
): AppThunk => runSearch({ kind: "location", stateId, lgaId, serviceId }, 0);

export const searchProvidersNearby = (
  lat: number,
  lon: number,
  skillName: string
): AppThunk => runSearch({ kind: "nearby", lat, lon, skillName }, 0);

/** Fetches the next page of the most recent search. */
export const loadMoreProviderResults = (): AppThunk => (dispatch, getState) => {
  const { lastQuery, resultsPageInfo } = getState().provider;
  if (!lastQuery || resultsPageInfo.last) return;
  return dispatch(runSearch(lastQuery, resultsPageInfo.page + 1));
};

export const fetchProviderById =
  (providerId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(providerActions.startLoading());
      const data = await apiFetch(`/api/providers/${providerId}`);
      dispatch(providerActions.setSelectedProvider(data));
    } catch (e: any) {
      dispatch(providerActions.setError(e.message));
    } finally {
      dispatch(providerActions.endLoading());
    }
  };
